import React, { useState } from 'react';
import { ArrowLeft, AlertTriangle, X, Plus, Upload, Merge, Loader2 } from 'lucide-react';
import { Report } from '../../types';
import { SifaMitarbeiter } from '../../services/sifa/types';
import { zusammenfuehren } from '../../services/sifa/sifaService';
import { describeError } from '../../services/errorMessage';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useMitarbeiter } from './useMitarbeiter';
import { MitarbeiterListe } from './MitarbeiterListe';
import { MitarbeiterDialog } from './MitarbeiterDialog';
import { MitarbeiterImportDialog } from './MitarbeiterImportDialog';

interface Props {
  project: { id: string; name?: string; data: Report };
  onBack: () => void;
  isReadOnly?: boolean;
}

/**
 * Mitarbeiterstamm eines SiFa-Projekts — duenner Container.
 *
 * Liste mit Suche und Faelligkeitsampel, Anlegen/Bearbeiten mit
 * Dubletten-Vorschau, CSV-Import und Zusammenfuehren zweier Zeilen (RPC
 * sifa_mitarbeiter_zusammenfuehren: Teilnahmen umhaengen, Quelle markieren).
 * Kein onUpdate: dieses Modul schreibt nie ins Projekt-JSON.
 */
export const MitarbeiterModul: React.FC<Props> = ({ project, onBack, isReadOnly = false }) => {
  const isMobile = useIsMobile();
  const meta = project.data?.metadata;
  const { mitarbeiter, teilnahmen, unterweisungen, laden, fehler, neuLaden, uebernehmen, uebernehmeViele } = useMitarbeiter(project.id);

  const [dialog, setDialog] = useState<{ offen: boolean; person: SifaMitarbeiter | null }>({ offen: false, person: null });
  const [zeigeImport, setZeigeImport] = useState(false);
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [fuehrtZusammen, setFuehrtZusammen] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [meldungTon, setMeldungTon] = useState<'info' | 'fehler'>('info');

  const zeigeFehler = (text: string) => { setMeldungTon('fehler'); setMeldung(text); };
  const zeigeInfo = (text: string) => { setMeldungTon('info'); setMeldung(text); };

  const auswahlUmschalten = (id: string) => setAuswahl((prev) => {
    const neu = new Set(prev);
    if (neu.has(id)) neu.delete(id);
    else {
      // Hoechstens zwei: Zusammenfuehren ist ein Paar, kein Stapel.
      if (neu.size >= 2) neu.delete([...neu][0]);
      neu.add(id);
    }
    return neu;
  });

  /**
   * Zusammenfuehren: die JUENGERE Zeile geht in die AELTERE auf — die
   * aeltere traegt in der Regel die laengere Historie. Wer es anders will,
   * bekommt die Rueckfrage mit beiden Namen und kann abbrechen.
   */
  const zusammenfuehrenStarten = async () => {
    if (isReadOnly || fuehrtZusammen || auswahl.size !== 2) return;
    const [a, b] = [...auswahl].map((id) => mitarbeiter.find((m) => m.id === id)).filter(Boolean) as SifaMitarbeiter[];
    if (!a || !b) return;
    const [ziel, quelle] = Date.parse(a.createdAt) <= Date.parse(b.createdAt) ? [a, b] : [b, a];
    const ok = window.confirm(
      `„${quelle.vorname} ${quelle.nachname}" in „${ziel.vorname} ${ziel.nachname}" zusammenführen?\n\n` +
      'Alle Teilnahmen der ersten Person werden der zweiten zugeordnet; die erste Zeile verschwindet aus der Liste. Das lässt sich nicht rückgängig machen.',
    );
    if (!ok) return;
    setFuehrtZusammen(true);
    setMeldung(null);
    try {
      const anzahl = await zusammenfuehren(quelle.id, ziel.id);
      setAuswahl(new Set());
      zeigeInfo(`Zusammengeführt: ${anzahl} Teilnahme(n) auf ${ziel.vorname} ${ziel.nachname} übertragen.`);
      await neuLaden();
    } catch (e) {
      zeigeFehler(describeError(e));
    } finally {
      setFuehrtZusammen(false);
    }
  };

  return (
    <div data-glass-view="sifa" className="flex flex-col h-full bg-slate-50">
      <div data-glass="kopf" className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shrink-0">
        <button onClick={onBack} className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-slate-100 rounded-full transition-colors">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-bold text-slate-800 truncate">Mitarbeiter</h1>
          <p className="text-xs text-slate-500 truncate">{project.name || meta?.projectName} · {mitarbeiter.length} im Stamm</p>
        </div>
        {!isReadOnly && (
          <div className="flex gap-2">
            {auswahl.size === 2 && (
              <button onClick={() => void zusammenfuehrenStarten()} disabled={fuehrtZusammen} className="min-h-[44px] px-3 flex items-center gap-2 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 hover:bg-amber-100 rounded-xl disabled:opacity-40">
                {fuehrtZusammen ? <Loader2 className="w-4 h-4 animate-spin" /> : <Merge className="w-4 h-4" />} Zusammenführen
              </button>
            )}
            {!isMobile && (
              <button onClick={() => setZeigeImport(true)} className="min-h-[44px] px-3 flex items-center gap-2 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl">
                <Upload className="w-4 h-4" /> CSV-Import
              </button>
            )}
            <button onClick={() => setDialog({ offen: true, person: null })} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-700 rounded-xl">
              <Plus className="w-4 h-4" /> Anlegen
            </button>
          </div>
        )}
      </div>

      <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${isMobile ? 'pb-20' : ''}`}>
        {isReadOnly && (
          <div className="p-3 bg-amber-50 border border-amber-300 rounded-xl text-xs text-amber-900">
            Nur-Ansicht: Dieses Projekt ist schreibgeschützt geöffnet. Der Stamm lässt sich ansehen — anlegen, ändern, importieren und zusammenführen ist gesperrt.
          </div>
        )}
        {(meldung || fehler) && (
          <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${meldungTon === 'fehler' || fehler ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
            {(meldungTon === 'fehler' || fehler) && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
            <span className="flex-1">{meldung || fehler}</span>
            {meldung && <button onClick={() => setMeldung(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>}
          </div>
        )}
        {!isReadOnly && !isMobile && auswahl.size === 1 && (
          <p className="text-[11px] text-slate-500">Eine zweite Zeile wählen, um beide zusammenzuführen.</p>
        )}

        <MitarbeiterListe
          mitarbeiter={mitarbeiter}
          teilnahmen={teilnahmen}
          unterweisungen={unterweisungen}
          laden={laden}
          isReadOnly={isReadOnly}
          isMobile={isMobile}
          auswahl={auswahl}
          onAuswahl={auswahlUmschalten}
          onBearbeiten={(m) => setDialog({ offen: true, person: m })}
        />
      </div>

      {dialog.offen && (
        <MitarbeiterDialog
          key={dialog.person?.id || 'neu'}
          projectId={project.id}
          mitarbeiter={dialog.person}
          bestand={mitarbeiter}
          isReadOnly={isReadOnly}
          onClose={() => setDialog({ offen: false, person: null })}
          onGespeichert={(m) => { uebernehmen(m); setDialog({ offen: false, person: null }); zeigeInfo(`${m.vorname} ${m.nachname} gespeichert.`); }}
          onWechseln={(m) => setDialog({ offen: true, person: m })}
        />
      )}

      {zeigeImport && (
        <MitarbeiterImportDialog
          projectId={project.id}
          bestand={mitarbeiter}
          onClose={() => setZeigeImport(false)}
          onFertig={(neue) => { uebernehmeViele(neue); setZeigeImport(false); zeigeInfo(`${neue.length} Person(en) importiert.`); }}
        />
      )}
    </div>
  );
};
